// Libraries
import _ from 'lodash';

// Supermove
import {gql} from '@supermove/graphql';
import {AccountGrantModel, NylasGrantProvider, UserModel} from '@supermove/models';
import {Config} from '@supermove/sdk';
import {withFragment} from '@supermove/utils';

// App
import EmailSenderKind, {
  EmailSenderKindType,
} from '@shared/modules/Organization/enums/EmailSenderKind';
import UserRole from '@shared/modules/User/enums/UserRole';

const navigateToNylasOauthUrl = (redirectUri: string) => {
  const clientId: string = Config.getNylasClientId();
  const nylasUrl = new URL('https://api.us.nylas.com/v3/connect/auth');
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    access_type: 'online',
  });

  // Redirect the user to the Nylas auth url.
  nylasUrl.search = params.toString();
  window.location.href = nylasUrl.toString();
};

export type AccountGrantInfoType = {
  hasExpiredCompanyAccount: boolean;
  hasExpiredUserAccount: boolean;
  hasActiveCompanyAccount: boolean;
  hasActiveUserAccount: boolean;
  hasEditPermissions: boolean;
  hasSendableAccountGrant: boolean;
  userAccountDaysUntilExpiration?: number;
  companyAccountDaysUntilExpiration?: number;
  expiredCompanyAccountStatusUpdatedAt?: string;
  expiredUserAccountStatusUpdatedAt?: string;
  emailSenderKind: EmailSenderKindType;
};

const accountGrantInfo = withFragment<UserModel, AccountGrantInfoType, unknown>(
  (viewer?: UserModel) => {
    if (!viewer) {
      return {
        hasExpiredCompanyAccount: false,
        hasExpiredUserAccount: false,
        hasActiveCompanyAccount: false,
        hasActiveUserAccount: false,
        hasSendableAccountGrant: false,
        userAccountDaysUntilExpiration: undefined,
        companyAccountDaysUntilExpiration: undefined,
        hasEditPermissions: false,
        emailSenderKind: EmailSenderKind.ORGANIZATION,
      };
    }
    const hasEditPermissions = _.includes(UserRole.ADMIN_ROLES_PLUS_SUPER, viewer.role);
    const organization = viewer.viewingOrganization;
    const {emailSenderKind} = organization.settings;
    const {isEnabledAccountGrantExpired} = organization.features;
    const hasExpiredCompanyAccount = !!organization.expiredAccountGrant?.id;
    const hasActiveCompanyAccount = !!organization.activeAccountGrant?.id;
    const sendWithSupermove = emailSenderKind === EmailSenderKind.SUPERMOVE;
    // If sending through the organization, the user account is irrelevant
    if (organization.settings.emailSenderKind === EmailSenderKind.ORGANIZATION) {
      return {
        hasExpiredCompanyAccount: isEnabledAccountGrantExpired && hasExpiredCompanyAccount,
        hasActiveCompanyAccount: hasActiveCompanyAccount && !sendWithSupermove,
        hasExpiredUserAccount: false,
        hasActiveUserAccount: false,
        hasSendableAccountGrant: hasActiveCompanyAccount && !sendWithSupermove,
        userAccountDaysUntilExpiration: undefined,
        companyAccountDaysUntilExpiration: isEnabledAccountGrantExpired
          ? organization.activeAccountGrant?.daysUntilExpiration
          : undefined,
        hasEditPermissions,
        expiredCompanyAccountStatusUpdatedAt: isEnabledAccountGrantExpired
          ? organization.expiredAccountGrant?.statusUpdatedAt
          : undefined,
        emailSenderKind,
      };
    }

    const hasExpiredUserAccount = !!viewer.expiredAccountGrant?.id;
    const hasActiveUserAccount = !!viewer.activeAccountGrant?.id;
    return {
      hasExpiredCompanyAccount: isEnabledAccountGrantExpired && hasExpiredCompanyAccount,
      hasActiveCompanyAccount: hasActiveCompanyAccount && !sendWithSupermove,
      hasExpiredUserAccount: isEnabledAccountGrantExpired && hasExpiredUserAccount,
      hasActiveUserAccount: hasActiveUserAccount && !sendWithSupermove,
      hasEditPermissions,
      hasSendableAccountGrant:
        !sendWithSupermove && (hasActiveCompanyAccount || hasActiveUserAccount),
      userAccountDaysUntilExpiration: isEnabledAccountGrantExpired
        ? viewer.activeAccountGrant?.daysUntilExpiration
        : undefined,
      companyAccountDaysUntilExpiration: isEnabledAccountGrantExpired
        ? organization.activeAccountGrant?.daysUntilExpiration
        : undefined,
      expiredUserAccountStatusUpdatedAt: isEnabledAccountGrantExpired
        ? viewer.expiredAccountGrant?.statusUpdatedAt
        : undefined,
      expiredCompanyAccountStatusUpdatedAt: isEnabledAccountGrantExpired
        ? organization.expiredAccountGrant?.statusUpdatedAt
        : undefined,
      emailSenderKind,
    };
  },
  gql`
    fragment AccountGrant_accountGrantInfo on User {
      id
      role
      activeAccountGrant {
        id
        daysUntilExpiration
      }
      expiredAccountGrant {
        id
        statusUpdatedAt
      }
      viewingOrganization {
        id
        features {
          isEnabledAccountGrantExpired: isEnabled(feature: "ACCOUNT_GRANT_EXPIRED")
        }
        settings {
          id
          emailSenderKind
        }
        activeAccountGrant {
          id
          daysUntilExpiration
        }
        expiredAccountGrant {
          id
          statusUpdatedAt
        }
      }
    }
  `,
);

const nylasProviderToDisplay: Record<NylasGrantProvider, string> = {
  microsoft: 'Microsoft',
  google: 'Google',
};

const getProviderDisplay = withFragment<AccountGrantModel, string | undefined>(
  (accountGrant?: AccountGrantModel) => {
    if (accountGrant?.provider === 'NYLAS') {
      return nylasProviderToDisplay[accountGrant.nylasGrantProvider];
    }
    return undefined;
  },
  gql`
    fragment AccountGrant_getProviderDisplay on AccountGrant {
      id
      provider
      nylasGrantProvider
    }
  `,
);

const AccountGrant = {
  navigateToNylasOauthUrl,
  accountGrantInfo,
  getProviderDisplay,
};

export default AccountGrant;
